import { useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';



import { Stack } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { Trans } from 'react-i18next';
import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';
import {
  IMeeting,
  MeetingRecordingStatus,
  MeetingTranscriptStatus,
  Phrase,
  RecordingTranscript,
  TranscriptSegment
} from 'src/services/meetings/meetings.types';


type RecordingSectionProps = {
  meeting?: IMeeting;
};

function RecordingSection({ meeting }: RecordingSectionProps) {

  const { t } = useTranslate();

  const [recordingTab, setRecordingTab] = useState<'transcript' | 'summary'>('transcript');

  const theme = useTheme();

  const handleChangeRecordingTab = (_event: React.SyntheticEvent, newValue: 'transcript' | 'summary') => {
    setRecordingTab(newValue);
  };

  const renderRecordingStatus = () => {
    if (!meeting) return null;

    const { recording_status, recording_fail_reason } = meeting;

    switch (recording_status) {
      case MeetingRecordingStatus.UNAVAILABLE:
        return (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('meetings.recording.unavailable')}
          </Typography>
        );
      case MeetingRecordingStatus.PROCESSING:
        return (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('meetings.recording.processing')}
          </Typography>
        );
      case MeetingRecordingStatus.FAILED:
        return (
          <Alert severity="error" sx={{ m: 3 }}>
            <Trans>
              {t('meetings.recording.failed_reason', { reason: recording_fail_reason || t('meetings.recording.default_fail_reason') })}
            </Trans>
          </Alert>
        );
      case MeetingRecordingStatus.AVAILABLE:
        if (recording_fail_reason) {
          return (
            <>
              <Alert severity="warning" sx={{ m: 3 }}>
                {recording_fail_reason}
              </Alert>
              <Box>
                <video controls style={{ width: '100%', height: 'auto', borderRadius: theme.shape.borderRadius }}>
                  <track default kind="captions" srcLang="en" />
                  <source src={meeting.recording?.file?.public_path} type="video/mp4" />
                  Your browser does not support the video.
                </video>
              </Box>
            </>
          );
        }
        return (
          <Box>
            <video controls style={{ width: '100%', height: 'auto', borderRadius: theme.shape.borderRadius }}>
              <track default kind="captions" srcLang="en" />
              <source src={meeting.recording?.file?.public_path} type="video/mp4" />
              Your browser does not support the video.
            </video>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderTranscriptContent = () => {
    if (!meeting) return null;

    const { transcript_status, transcribe_fail_reason } = meeting;

    switch (transcript_status) {
      case MeetingTranscriptStatus.UNAVAILABLE:
        return (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('meetings.transcript.unavailable')}
            </Typography>
          </Paper>
        );
      case MeetingTranscriptStatus.PROCESSING:
        return (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('meetings.transcript.processing')}
            </Typography>
          </Paper>
        );
      case MeetingTranscriptStatus.FAILED:
        return (
          <Alert severity="error" sx={{ m: 3 }}>
            <Trans>
              {t('meetings.transcript.failed_reason', { reason: transcribe_fail_reason || t('meetings.transcript.default_fail_reason') })}
            </Trans>
          </Alert>
        );
      case MeetingTranscriptStatus.AVAILABLE:
        return (
          <Box>
            {
              (meeting.transcript?.content && Array.isArray(meeting.transcript.content) && meeting.transcript.content.length > 0) ? (
                <TranscriptRenderBox transcript={meeting.transcript.content as RecordingTranscript} />
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('meetings.transcript.unavailable')}
                  </Typography>
                </Box>
              )}
          </Box>
        );
      default:
        return null;
    }
  };

  const shareRecording = () => {
    if (!meeting?.recording?.file?.public_path) return;
    navigator.clipboard.writeText(meeting.recording.file.public_path)
      .then(() => {
        enqueueSnackbar(t('common.copied_to_clipboard'), {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar(t('errors.default_error'), {
          variant: 'error',
        });
      });
  }

  const renderSummaryContent = () => {
    if (!meeting) return null;

    const { transcript_status, transcript } = meeting;

    if (transcript_status !== MeetingTranscriptStatus.AVAILABLE || !transcript) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('meetings.summary.unavailable')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box>
        {transcript.summary ? (
          <Typography variant="body2">
            {transcript.summary}
          </Typography>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('meetings.summary.unavailable')}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardHeader title={
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
          <Typography variant="h6">{t('meetings.recording.title')}</Typography>

          <Stack direction="row" alignItems="center">
            {meeting?.recording?.file && (
              <Tooltip title={t('meetings.recording.download')}>
                <IconButton
                  href={meeting.recording?.file?.public_path}
                  download
                  target="_blank"
                  rel="noopener"
                >
                  <Iconify icon="icon-park-outline:download-three" width={20} height={20} />
                </IconButton>
              </Tooltip>
            )}

            {meeting?.recording?.file && (
              <Tooltip title={t('meetings.recording.share')}>
                <IconButton
                  onClick={shareRecording}
                >
                  <Iconify icon="ph:share-network-duotone" width={20} height={20} />
                </IconButton>
              </Tooltip>
            )}

          </Stack>
        </Stack>
      }
      />
      <Box sx={{ p: 3 }}>
        <Paper sx={{ minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: theme.palette.background.neutral }}>
          {renderRecordingStatus()}
        </Paper>
      </Box>

      <Divider />

      <Box sx={{ px: 3 }}>
        <Tabs value={recordingTab} onChange={handleChangeRecordingTab}>
          <Tab value="transcript" label={t('meetings.detail.tabs.transcript')} />
          <Tab value="summary" label={t('meetings.detail.tabs.summary')} />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {recordingTab === 'transcript' && renderTranscriptContent()}
        {recordingTab === 'summary' && renderSummaryContent()}
      </Box>
    </Card>
  );
}

export default RecordingSection;

export function TranscriptRenderBox({ transcript }: { transcript: RecordingTranscript }) {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Calculate the height of a single message (approximate)
  const messageHeight = 120; // Approximate height in pixels
  const blurHeight = 30; // Height of the blur effect
  const visibleMessages = 5;
  const containerHeight = messageHeight * visibleMessages;

  const mergedTranscript: TranscriptSegment[] = useMemo(() => {
    const segments: TranscriptSegment[] = [];

    if (!transcript || !Array.isArray(transcript)) return segments;

    transcript.forEach((segment: TranscriptSegment) => {
      if (segments.length > 0 &&
        segments[segments.length - 1].participant.name === segment.participant.name) {
        // Join phrases from consecutive segments
        segments[segments.length - 1].phrase.push(...segment.phrase);
      } else {
        // Create a shallow copy to avoid mutating original
        segments.push({
          participant: { ...segment.participant },
          phrase: [...segment.phrase],
        });
      }
    });

    return segments;
  }, [transcript]);

  function formatSeconds(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  // We'll use a different approach for the blur effect
  // Instead of toggling based on scroll state, we'll use CSS gradients
  // that are always present but only visible when content is scrolled

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        height: containerHeight,
        overflowY: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.grey[200],
        },
      }}
    >
      {/* Top blur overlay - always present but only visible when scrolled */}
      {mergedTranscript.length > visibleMessages && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: blurHeight,
            background: `linear-gradient(to bottom, ${theme.palette.background.paper}, transparent)`,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.8,
          }}
        />
      )}

      <Stack spacing={2} sx={{ py: 1 }}>
        {mergedTranscript.map((segment, index) => {
          // Concatenate all phrases into a single text block
          const fullText = segment.phrase.map((phrase: Phrase) => phrase.text).join(' ');
          // Assume phrases are in chronological order; use first start and last end
          const startTimestamp = segment.phrase[0]?.start_timestamp;
          const endTimestamp = segment.phrase[segment.phrase.length - 1]?.end_timestamp;

          return (
            <Box
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                padding: 2
              }}
            >
              <Stack spacing={1} alignItems="center" justifyContent="space-between" direction="row" width="100%" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {segment.participant.name}
                </Typography>

                {
                  segment.participant.is_host && (
                    <Chip
                      label="Host"
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
              </Stack>

              <Typography variant="body2" sx={{ mt: 1 }}>
                {fullText}
              </Typography>
              {startTimestamp && endTimestamp && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {formatSeconds(startTimestamp.relative)} - {formatSeconds(endTimestamp.relative)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Bottom blur overlay - always present but only visible when scrolled */}
      {mergedTranscript.length > visibleMessages && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            height: blurHeight,
            background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
            zIndex: 1,
            pointerEvents: 'none',
            marginTop: `-${blurHeight}px`,
            opacity: 0.8,
          }}
        />
      )}
    </Box>
  );
}